import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, MailPlus, RefreshCw, Trash2 } from 'lucide-react';
import { useT } from '@/lib/i18n/useT';
import { firstInvalidControl } from '@/lib/i18n/nativeValidation';
import { toast } from '@/components/ui/Toast';
import { useFeatureAccess } from '@onxite/plan-sdk';
import { reporterAdminApi } from './adminApi';
import type { ReporterListRow, InviteResult } from './adminApi';

/**
 * PRD §12 — Reporter admin panel mounted in the Project Environment.
 *
 * Replaces the "Reporter View ships in Phase 2" placeholder once the
 * `co_reporter*` tables ship. Three responsibilities:
 *   1. Trigger an invite (modal → `POST /co-reporter/invite`).
 *   2. Show the reporters scoped to this event with link state + expiresAt.
 *   3. Reissue / revoke each link inline.
 *
 * Gated by `useFeatureAccess('reporterRole')` — free tier hides the entire
 * panel, paid tiers see "Free · Unlimited" copy.
 */
export function ReportersPanel({ eventId }: { eventId: number }) {
  const t = useT();
  const access = useFeatureAccess('reporterRole');
  const [inviteOpen, setInviteOpen] = useState(false);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['reporters', 'event', eventId],
    queryFn: () => reporterAdminApi.listForEvent(eventId),
    enabled: !!eventId && access.enabled,
  });

  const revoke = useMutation({
    mutationFn: (id: number) => reporterAdminApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reporters', 'event', eventId] });
      toast({ tone: 'success', title: t('reporterAdmin.revoked') });
    },
  });

  const reissue = useMutation({
    mutationFn: (id: number) => reporterAdminApi.reissue(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reporters', 'event', eventId] });
      copyToClipboard(data.magicUrl);
      toast({ tone: 'success', title: t('reporterAdmin.reissuedCopied') });
    },
  });

  if (access.isLoading) return null;

  return (
    <div className="rounded-2xl border border-[var(--color-xk-border)] p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {t('reporterAdmin.eyebrow')}
            {access.enabled && (
              <span className="ml-2 rounded-full bg-[var(--color-accent-15)] px-2 py-0.5 text-[9px] font-extrabold text-[var(--color-accent-ink)]">
                {t('reporterAdmin.freeUnlimited')}
              </span>
            )}
          </p>
          <h3 className="mt-0.5 text-sm font-bold text-[var(--color-xk-lavender)]">
            {t('reporterAdmin.title')}
          </h3>
        </div>
        {access.enabled ? (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-xk-accent)] px-3 py-1.5 text-xs font-bold text-[var(--color-fg-on-accent)]"
          >
            <MailPlus className="h-3.5 w-3.5" />
            {t('reporterAdmin.invite')}
          </button>
        ) : (
          <span className="rounded-full bg-[var(--color-accent-15)] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-accent-ink)]">
            {t('reporterAdmin.paidOnly')}
          </span>
        )}
      </div>

      {!access.enabled ? (
        <p className="mt-3 text-xs leading-relaxed text-[var(--color-xk-text-muted)]">
          {t('reporterAdmin.paidHint')}
        </p>
      ) : list.isLoading ? (
        <p className="mt-3 text-xs text-[var(--color-xk-text-muted)]">
          {t('reporterAdmin.loading')}
        </p>
      ) : (list.data?.length ?? 0) === 0 ? (
        <p className="mt-3 text-xs text-[var(--color-xk-text-muted)]">
          {t('reporterAdmin.empty')}
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--color-xk-border)]">
          {list.data!.map((r) => (
            <ReporterRow
              key={r.id}
              row={r}
              onRevoke={() => revoke.mutate(r.id)}
              onReissue={() => reissue.mutate(r.id)}
              busy={revoke.isPending || reissue.isPending}
            />
          ))}
        </ul>
      )}

      {inviteOpen && (
        <InviteReporterDialog
          eventId={eventId}
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            queryClient.invalidateQueries({
              queryKey: ['reporters', 'event', eventId],
            });
          }}
        />
      )}
    </div>
  );
}

function ReporterRow({
  row,
  onRevoke,
  onReissue,
  busy,
}: {
  row: ReporterListRow;
  onRevoke: () => void;
  onReissue: () => void;
  busy: boolean;
}) {
  const t = useT();
  const stateColor: Record<ReporterListRow['linkState'], string> = {
    active: 'var(--color-success)',
    revoked: 'var(--color-error)',
    expired: 'var(--color-warn)',
  };
  return (
    <li className="flex items-center gap-3 py-2">
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ background: stateColor[row.linkState] }}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-[var(--color-xk-lavender)]">
          {row.name}
        </p>
        <p className="truncate text-[11px] text-[var(--color-xk-text-muted)]">
          {row.email} · {t(`reporterAdmin.state.${row.linkState}`)}{' '}
          {row.linkState === 'active' && (
            <>
              ·{' '}
              {t('reporterAdmin.expiresAt', {
                date: new Date(row.expiresAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              })}
            </>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={onReissue}
        disabled={busy}
        title={t('reporterAdmin.reissueHint')}
        className="rounded-md p-1.5 text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-xk-lavender)] disabled:opacity-40"
      >
        <RefreshCw className="h-3.5 w-3.5" />
      </button>
      {row.linkState === 'active' && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={busy}
          title={t('reporterAdmin.revokeHint')}
          className="rounded-md p-1.5 text-[var(--color-error)] hover:bg-[var(--color-xk-surface)] disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

function InviteReporterDialog({
  eventId,
  onClose,
  onInvited,
}: {
  eventId: number;
  onClose: () => void;
  onInvited: () => void;
}) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [result, setResult] = useState<InviteResult | null>(null);
  // Aviso de validación EN LÍNEA, no en toast: este diálogo (z-[80]) tapa el
  // visor de toasts (z-[60]) y el aviso quedaría bajo el velo.
  const [aviso, setAviso] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      reporterAdminApi.invite({
        eventId,
        email: email.trim(),
        name: name.trim() || undefined,
      }),
    onSuccess: (data) => {
      setResult(data);
      copyToClipboard(data.magicUrl);
      toast({ tone: 'success', title: t('reporterAdmin.invitedCopied') });
      onInvited();
    },
    onError: (e: any) => {
      // F3c (PRD §2.1): al muro del cap de Reviewers, un upsell — no un error
      // a secas. El backend manda 409 { code: 'PLAN_LIMIT_EXCEEDED' }.
      const code = e?.code ?? e?.data?.code;
      if (e?.status === 409 || code === 'PLAN_LIMIT_EXCEEDED') {
        toast({
          tone: 'info',
          title: t('reporterAdmin.capReached'),
          description: t('reporterAdmin.capReachedBody'),
        });
        return;
      }
      toast({ tone: 'error', title: e?.message ?? t('reporterAdmin.inviteError') });
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-6 text-[var(--color-xk-lavender)]"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[10px] uppercase tracking-widest opacity-60">
          {t('reporterAdmin.modal.eyebrow')}
        </p>
        <h2 className="mt-1 text-lg font-bold">
          {t('reporterAdmin.modal.title')}
        </h2>
        <p className="mt-2 text-sm opacity-80">{t('reporterAdmin.modal.body')}</p>

        {!result ? (
          <form
            noValidate
            className="mt-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              const invalido = firstInvalidControl(e.currentTarget, t);
              if (invalido) {
                setAviso(invalido.message);
                return;
              }
              setAviso(null);
              if (email.trim()) invite.mutate();
            }}
          >
            <label className="block text-sm">
              <span className="text-[10px] uppercase tracking-widest opacity-60">
                {t('reporterAdmin.modal.email')}
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setAviso(null);
                }}
                aria-invalid={aviso ? true : undefined}
                placeholder="ba@brand.com"
                className="mt-1 w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-sm"
              />
              {aviso && (
                <p role="alert" className="mt-1 text-[11px] text-[var(--color-error)]">
                  {aviso}
                </p>
              )}
            </label>
            <label className="block text-sm">
              <span className="text-[10px] uppercase tracking-widest opacity-60">
                {t('reporterAdmin.modal.nameOptional')}
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('reporterAdmin.modal.namePlaceholder')}
                className="mt-1 w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-sm"
              />
            </label>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full px-4 py-2 text-sm text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={!email.trim() || invite.isPending}
                className="rounded-full bg-[var(--color-xk-accent)] px-4 py-2 text-sm font-bold text-[var(--color-fg-on-accent)] disabled:opacity-50"
              >
                {invite.isPending
                  ? t('reporterAdmin.modal.sending')
                  : t('reporterAdmin.modal.send')}
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 space-y-3">
            <p className="text-sm">
              {t('reporterAdmin.modal.successTo', { email: result.email })}
            </p>
            <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3">
              <p className="text-[10px] uppercase tracking-widest opacity-60">
                {t('reporterAdmin.modal.magicUrl')}
              </p>
              <code className="mt-1 block break-all text-[12px] text-[var(--color-accent-ink)]">
                {result.magicUrl}
              </code>
              <button
                type="button"
                onClick={() => {
                  copyToClipboard(result.magicUrl);
                  toast({ tone: 'success', title: t('reporterAdmin.copied') });
                }}
                className="mt-2 inline-flex items-center gap-1 rounded-md border border-[var(--color-xk-border)] px-2 py-1 text-[11px] font-bold"
              >
                <Copy className="h-3 w-3" />
                {t('reporterAdmin.copy')}
              </button>
            </div>
            <p className="text-[11px] text-[var(--color-xk-text-muted)]">
              {t('reporterAdmin.modal.expiresHint', {
                date: new Date(result.expiresAt).toLocaleString(),
              })}
            </p>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full bg-[var(--color-xk-accent)] px-4 py-2 text-sm font-bold text-[var(--color-fg-on-accent)]"
              >
                {t('common.done')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function copyToClipboard(text: string) {
  try {
    navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}
